// These enums correspond to the dropdown options we are getting from Contentful

export enum Margins {
  NoTop = 'No top margin',
  NoBottom = 'No bottom margin',
  NoMargins = 'No margins',
  Centered = 'Centered',
}

export enum RoundedCorners {
  None = 'None',
  Top = 'Top',
  Bottom = 'Bottom',
  All = 'All',
}

export enum Colorways {
  Primary = 'Primary',
  Secondary = 'Secondary',
  Tertiary = 'Tertiary',
  Inverse = 'Inverse',
  DarkestGreen = 'Darkest Green',
  DarkBlue = 'Dark Blue',
  ActionPrimary = 'Action Primary',
  AccentBlue100 = 'Accent/Blue 100 (Tax Use Only)',
  AccentBlue200 = 'Accent/Blue 200',
  AccentYellow200 = 'Accent/Yellow 200',
  AccentYellow300 = 'Accent/Yellow 300',
  AccentRed300 = 'Accent/Red 300',
  AccentRed500 = 'Accent/Red 500',
  AccentPink200 = 'Accent/Pink 200',
  AccentPink600 = 'Accent/Pink 600',
  Yellow = 'CE-Yellow (CE Use Only)',
  DarkGreenGradient = 'Dark Gradient',
  DarkGreenGradientLeftToRight = 'Dark Gradient - Left to Right',
  DarkGreenAnimation = 'Dark Green Animation',
  ChimePlus = 'Chime+',
  Verde = 'Verde',
  Reserve = 'Reserve',
  Nickel = 'Nickel',
  Custom = 'Custom',
}

export enum KeyLinePosition {
  Top = 'Top',
  Bottom = 'Bottom',
  None = 'None',
  Between = 'Between',
}

export enum CopyPosition {
  Center = 'Center',
  Bottom = 'Bottom',
}

export enum CopyJustification {
  Left = 'Left',
  Right = 'Right',
  Center = 'Center',
}

export enum Alignment {
  Left = 'Media left, copy right',
  Right = 'Media right, copy left',
  Full = 'Media full bleed',
}

export enum MobileAlignment {
  MediaAboveCopy = 'Media above copy',
  MediaBelowCopy = 'Media below copy',
}

export enum BentoBoxLayout {
  TwoByTwo = '2x2',
  ThreeByTwo = '3x2',
}

export enum CalloutColumns {
  Two = 'Two',
  Three = 'Three',
  Four = 'Four',
  Five = 'Five',
}

export enum PrebuiltHeadlineText {
  MillionsTrustChime = 'Millions of people trust Chime.',
  MakeMoneyGrowFast = 'Make your money grow fast.',
  MostLovedBankingApp = 'The #1 most loved banking app',
  MaxRefund = 'Your max refund is waiting.',
  UnlockFinancialProgress = 'Unlock Financial Progress',
  MakeTheSwitchToday = 'Make the switch today.',
  SignUpForDollarsSense = 'Sign up for Dollars & Sense',
  YourMoneyShouldntCostMoney = "Your money shouldn't cost money.",
}

export enum ImageWidth {
  Contained = 'Contained',
  FullWidth = 'Full width',
}

export type CardStyle = 'Stepped' | 'Paginated' | 'None' | string;

export enum BackgroundColor {
  White = 'white-colorway',
  LightGreen = 'light-green-colorway',
  MediumGreen = 'medium-green-colorway',
  BrightGreen = 'bright-green-colorway',
  DarkBlue = 'dark-blue-colorway',
  DarkGreen = 'dark-green-colorway',
  DarkestGreen = 'darkest-green-colorway',
  Yellow = 'yellow-colorway',
  AccentBlue100 = 'accent-blue-100-colorway',
  AccentBlue200 = 'accent-blue-200-colorway',
  AccentYellow200 = 'accent-yellow-200-colorway',
  AccentYellow300 = 'accent-yellow-300-colorway',
  AccentRed300 = 'accent-red-300-colorway',
  AccentRed500 = 'accent-red-500-colorway',
  AccentPink200 = 'accent-pink-200-colorway',
  AccentPink600 = 'accent-pink-600-colorway',
  DarkGreenGradient = 'dark-gradient-colorway',
  DarkGreenGradientLeftToRight = 'dark-gradient-colorway-ltr',
  DarkGreenAnimation = 'dark-green-animation-colorway',
  ChimePlus = 'chime-plus-colorway',
  Verde = 'verde-colorway',
  Reserve = 'reserve-colorway',
  Nickel = 'nickel-colorway',
}

export enum EnterpriseBackgroundColor {
  DarkGreen = 'enterprise-dark-green-colorway',
}

export enum MediaContentTypes {
  Image = 'image',
  Video = 'video',
}

export enum CardTypes {
  Icon = 'CardsIconItem',
  Image = 'CardsImageItem',
  Video = 'CardsVideoItem',
}

export enum BentoBoxCardTypes {
  Standard = 'BentoBoxCard',
  AppReview = 'BentoBoxAppReviewCard',
}

export enum BentoBoxMainSectionOrientation {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export enum LinkTargets {
  SameTab = 'Open link in the same tab',
  NewTab = 'Open link in a new tab',
}

export enum MainNavLinkSize {
  Large = 'Large',
  Small = 'Small',
}

export enum CardLayout {
  TwoOnTop = '2 on top, 3 on bottom',
  ThreeOnTop = '3 on top, 2 on bottom',
}

export enum CtaStyles {
  Solid = 'solid',
  SolidInverse = 'solid inverse',
  Outline = 'outline',
  Link = 'link',
}

export enum CardsItemImageLayout {
  Horizontal = 'Horizontal',
  Vertical = 'Vertical',
}

export enum LayoutOrientation {
  Horizontal = 'Horizontal',
  Vertical = 'Vertical',
}

export enum Themes {
  Enterprise = 'enterprise',
  Referral = 'referral',
  Www = 'www',
}

export enum RadioOption {
  Yes = 'Yes',
  No = 'No',
}

export type NinetailedMode = 'client' | 'server';

export const MediaCopyVariants = {
  BASE: 'Base',
  COPY_SLIDER: 'Copy Slider',
  CONTAINED_BACKGROUND: 'Contained Background',
} as const;

export type MediaCopyVariant = (typeof MediaCopyVariants)[keyof typeof MediaCopyVariants];

export const CompareMobileDropdownVariants = {
  INLINE: 'Inline',
  BORDERED: 'Bordered',
} as const;

export type CompareMobileDropdownVariant =
  (typeof CompareMobileDropdownVariants)[keyof typeof CompareMobileDropdownVariants];

export enum CompareVariant {
  Table = 'Table',
  Graph = 'Graph',
}

export const NavVariants = {
  BASE: 'Base',
  BLOG: 'Blog',
  HORIZONTAL: 'Horizontal',
} as const;

export type NavVariant = (typeof NavVariants)[keyof typeof NavVariants];

export const HeaderLogoItemVariants = {
  IMAGE: 'Image',
  ILLUSTRATION: 'Illustration',
  ICON: 'Icon',
} as const;

export enum ImageAspectRatios {
  '1:1' = '1/1',
  '4:3' = '4/3',
}

export enum Breakpoints {
  Mobile = 750,
}

export enum BadgeOverlayAlignment {
  Left = 'Left',
  Right = 'Right',
}

export enum CopyAlignment {
  Left = 'Left',
  Centered = 'Centered',
}

export enum BulletStyle {
  Arrows = 'Arrows',
  CheckBoxes = 'Check Boxes',
}

export enum BulletListStyle {
  Arrow = 'Arrow',
  Bullet = 'Bullet',
  Dash = 'Dash',
  Checkmark = 'Checkmark',
}

export enum NotificationOverlayDesktopPosition {
  TopRight = 'Top - Right',
  MiddleRight = 'Middle - Right',
  BottomRight = 'Bottom - Right',
  TopLeft = 'Top - Left',
  MiddleLeft = 'Middle - Left',
  BottomLeft = 'Bottom - Left',
}

export enum LabelPlacement {
  Right = 'Right of Icon',
  Top = 'Above the Icon',
  Hidden = 'Do Not Display',
}

/**
 * Array of content type IDs in Contentful (used to fetch display names from Contentful's Management API)
 * The display names are fetched dynamically from Contentful, eliminating the need to maintain a manual mapping
 */
export const componentContentTypeIds = [
  'accordion',
  'animatedMarquee',
  'articleGrid',
  'author',
  'bentoBox',
  'calloutGrid',
  'calloutImage',
  'calloutIcon',
  'cardsImage',
  'cardsIcon',
  'cardsExpandable',
  'cardsStacked',
  'compare',
  'contentBlock',
  'copyAddOnWithButton',
  'customQueryStringParameter',
  'disclosureAddOn',
  'disclosureBanner',
  'disclosureGroupings',
  'faq',
  'featuredBanner',
  'features',
  'headerCta',
  'headerLogo',
  'heroCarousel',
  'heroConsolidated',
  'hyperlinkedMedia',
  'hubSpot',
  'impactHeadline',
  'keyline',
  'launchpad',
  'mapLocator',
  'mediaCopy',
  'nav',
  'note',
  'resourcesItgBranded',
  'richTextEditor',
  'siteFooter',
  'subnav',
  'teaserLinks',
  'testimonial',
  'wysiwyg',
] as const;

export type HeaderLogoItemVariant = (typeof HeaderLogoItemVariants)[keyof typeof HeaderLogoItemVariants];

export enum OverflowVisibleSettings {
  All = 'all',
  None = 'none',
  Desktop = 'desktop',
  Mobile = 'mobile',
  MobileVertical = 'mobile-vertical',
}

export const DefaultAnimationSettings = {
  autoplay: true,
  loop: true,
  controls: false,
  speed: 1,
};

export enum BentoBoxCardHeadlineSize {
  Default = 'Default',
  Large = 'Large',
  ExtraLarge = 'Extra Large',
}

export enum CareersTemplateType {
  Detail = 'Detail',
  Search = 'Search',
  SavedJobs = 'Saved Jobs',
}
